import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import { logActivity } from "./activityLog";
import { ToastContainer, showToast } from "./toast";
import DarkModeToggle from "./darkMode";
import ColumnSettings from "./columnSettings";
import AuditTrail from "./AuditTrail";
import DragDropContentManager from "./DragDropContentManager";
import AdvancedFilter from "./advancedFilter";
import { exportData, importData } from "./exportImport";
import { uploadImageFile } from "./firebaseUpload";
import ModalEdit from "./ModalEdit";
import "./App.css";

// Utility for simple URL validation
function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// Bulk selection hook
function useBulkSelection(items) {
  const [selected, setSelected] = useState([]);
  const toggle = id => setSelected(selected =>
    selected.includes(id) ? selected.filter(i => i !== id) : [...selected, id]
  );
  const selectAll = () => setSelected(items.map(i => i.id));
  const clearAll = () => setSelected([]);
  return { selected, toggle, selectAll, clearAll };
}

// DeityManager with full UI and features
function DeityManager({ onSelectDeity, columns }) {
  const [deities, setDeities] = useState([]);
  const [nameEn, setNameEn] = useState("");
  const [nameTa, setNameTa] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [show, setShow] = useState(true);

  // For editing
  const [editingId, setEditingId] = useState(null);
  const [editNameEn, setEditNameEn] = useState("");
  const [editNameTa, setEditNameTa] = useState("");
  const [editImageUrl, setEditImageUrl] = useState("");
  const [editShow, setEditShow] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Search/filter
  const [search, setSearch] = useState("");
  const filteredDeities = deities.filter(
    d =>
      d.nameEn.toLowerCase().includes(search.toLowerCase()) ||
      d.nameTa.toLowerCase().includes(search.toLowerCase())
  );

  // Confirmation dialog
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Bulk selection
  const bulk = useBulkSelection(filteredDeities);

  useEffect(() => {
    getDocs(collection(db, "deities")).then((snapshot) => {
      setDeities(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
  }, []);

  const addDeity = async (e) => {
    e.preventDefault();
    if (imageUrl && !isValidUrl(imageUrl)) {
      showToast("Please enter a valid image URL.", "error");
      return;
    }
    const docRef = await addDoc(collection(db, "deities"), {
      nameEn,
      nameTa,
      imageUrl,
      show,
    });
    await logActivity({
      action: "add",
      entity: "deity",
      entityId: docRef.id,
      details: { nameEn, nameTa, imageUrl, show },
      user: "admin"
    });
    showToast("Deity added!", "success");
    setNameEn("");
    setNameTa("");
    setImageUrl("");
    setShow(true);
    getDocs(collection(db, "deities")).then((snapshot) => {
      setDeities(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
  };

  const toggleShow = async (deity) => {
    await updateDoc(doc(db, "deities", deity.id), { show: !deity.show });
    await logActivity({
      action: "edit",
      entity: "deity",
      entityId: deity.id,
      details: { show: `${deity.show} -> ${!deity.show}` },
      user: "admin"
    });
    showToast("Deity visibility updated!", "info");
    setDeities((prev) =>
      prev.map((d) => (d.id === deity.id ? { ...d, show: !d.show } : d))
    );
  };

  const removeDeity = async (deityId) => {
    await deleteDoc(doc(db, "deities", deityId));
    await logActivity({
      action: "delete",
      entity: "deity",
      entityId: deityId,
      details: {},
      user: "admin"
    });
    showToast("Deity deleted!", "success");
    setDeities((prev) => prev.filter((d) => d.id !== deityId));
    setConfirmDeleteId(null);
  };

  // Bulk actions
  const bulkDelete = async () => {
    for (const id of bulk.selected) {
      await deleteDoc(doc(db, "deities", id));
      await logActivity({
        action: "bulk_delete",
        entity: "deity",
        entityId: id,
        details: {},
        user: "admin"
      });
    }
    showToast("Bulk delete completed!", "success");
    setDeities(deities.filter(d => !bulk.selected.includes(d.id)));
    bulk.clearAll();
  };

  const bulkHide = async () => {
    for (const id of bulk.selected) {
      await updateDoc(doc(db, "deities", id), { show: false });
      await logActivity({
        action: "bulk_hide",
        entity: "deity",
        entityId: id,
        details: { show: "true -> false" },
        user: "admin"
      });
    }
    showToast("Bulk hide completed!", "info");
    setDeities(deities.map(d => bulk.selected.includes(d.id) ? { ...d, show: false } : d));
    bulk.clearAll();
  };

  const bulkShow = async () => {
    for (const id of bulk.selected) {
      await updateDoc(doc(db, "deities", id), { show: true });
      await logActivity({
        action: "bulk_show",
        entity: "deity",
        entityId: id,
        details: { show: "false -> true" },
        user: "admin"
      });
    }
    showToast("Bulk show completed!", "info");
    setDeities(deities.map(d => bulk.selected.includes(d.id) ? { ...d, show: true } : d));
    bulk.clearAll();
  };

  // Edit functionality
  const startEdit = (deity) => {
    setEditingId(deity.id);
    setEditNameEn(deity.nameEn);
    setEditNameTa(deity.nameTa);
    setEditImageUrl(deity.imageUrl || "");
    setEditShow(deity.show);
    setEditModalOpen(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditModalOpen(false);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (editImageUrl && !isValidUrl(editImageUrl)) {
      showToast("Please enter a valid image URL.", "error");
      return;
    }
    await updateDoc(doc(db, "deities", editingId), {
      nameEn: editNameEn,
      nameTa: editNameTa,
      imageUrl: editImageUrl,
      show: editShow,
    });
    await logActivity({
      action: "edit",
      entity: "deity",
      entityId: editingId,
      details: { nameEn: editNameEn, nameTa: editNameTa, imageUrl: editImageUrl, show: editShow },
      user: "admin"
    });
    showToast("Deity updated!", "success");
    setEditingId(null);
    setEditModalOpen(false);
    getDocs(collection(db, "deities")).then((snapshot) => {
      setDeities(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
  };

  // File upload for icon
  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = await uploadImageFile(file, "deity_icons");
      setImageUrl(url);
      showToast("Icon uploaded!", "success");
    }
  };

  return (
    <div className="admin-container">
      <h2>Deities</h2>
      <input
        placeholder="Search deities..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 12, padding: 8, width: "100%" }}
      />
      <form onSubmit={addDeity} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input
          placeholder="Name (English)"
          value={nameEn}
          onChange={(e) => setNameEn(e.target.value)}
          required
          style={{ flex: "1 1 180px", padding: 8 }}
        />
        <input
          placeholder="Name (Tamil)"
          value={nameTa}
          onChange={(e) => setNameTa(e.target.value)}
          required
          style={{ flex: "1 1 180px", padding: 8 }}
        />
        <input
          placeholder="Image URL"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          style={{ flex: "1 1 180px", padding: 8 }}
        />
        <input
          type="file"
          accept="image/*"
          onChange={handleIconUpload}
          style={{ flex: "1 1 180px", padding: 8 }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
          Show
          <input
            type="checkbox"
            checked={show}
            onChange={(e) => setShow(e.target.checked)}
          />
        </label>
        <button type="submit" style={{ padding: "8px 16px" }}>Add Deity</button>
      </form>
      <div style={{ marginBottom: 12 }}>
        <button onClick={bulk.selectAll} style={{ marginRight: 8 }}>Select All</button>
        <button onClick={bulk.clearAll} style={{ marginRight: 8 }}>Clear Selection</button>
        <button onClick={bulkShow} disabled={bulk.selected.length === 0} style={{ marginRight: 8 }}>Bulk Show</button>
        <button onClick={bulkHide} disabled={bulk.selected.length === 0} style={{ marginRight: 8 }}>Bulk Hide</button>
        <button onClick={bulkDelete} disabled={bulk.selected.length === 0} style={{ color: "red" }}>Bulk Delete</button>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 24 }}>
        <thead>
          <tr style={{ background: "#f7f7f7" }}>
            <th></th>
            {columns.nameEn && <th style={{ padding: 8, border: "1px solid #ddd" }}>Name (English)</th>}
            {columns.nameTa && <th style={{ padding: 8, border: "1px solid #ddd" }}>Name (Tamil)</th>}
            {columns.icon && <th style={{ padding: 8, border: "1px solid #ddd" }}>Icon</th>}
            {columns.show && <th style={{ padding: 8, border: "1px solid #ddd" }}>Show</th>}
            {columns.actions && <th style={{ padding: 8, border: "1px solid #ddd" }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {filteredDeities.map((deity) => (
            <tr key={deity.id} style={{ background: deity.show ? "#fff" : "#f9e0e0" }}>
              <td>
                <input
                  type="checkbox"
                  checked={bulk.selected.includes(deity.id)}
                  onChange={() => bulk.toggle(deity.id)}
                />
              </td>
              {columns.nameEn && (
                <td style={{ padding: 8, border: "1px solid #ddd", cursor: "pointer" }} onClick={() => onSelectDeity(deity)}>
                  {deity.nameEn}
                </td>
              )}
              {columns.nameTa && (
                <td style={{ padding: 8, border: "1px solid #ddd", cursor: "pointer" }} onClick={() => onSelectDeity(deity)}>
                  {deity.nameTa}
                </td>
              )}
              {columns.icon && (
                <td style={{ padding: 8, border: "1px solid #ddd" }}>
                  {deity.imageUrl ? (
                    <img src={deity.imageUrl} alt="icon" style={{ width: 32, height: 32, borderRadius: 8 }} />
                  ) : (
                    <span style={{ color: "#aaa" }}>No icon</span>
                  )}
                </td>
              )}
              {columns.show && (
                <td style={{ padding: 8, border: "1px solid #ddd" }}>
                  <button onClick={() => toggleShow(deity)} style={{ padding: "4px 8px" }}>
                    {deity.show ? "Hide" : "Show"}
                  </button>
                </td>
              )}
              {columns.actions && (
                <td style={{ padding: 8, border: "1px solid #ddd" }}>
                  <button onClick={() => startEdit(deity)} style={{ padding: "4px 8px", color: "blue" }}>Edit</button>
                  <button onClick={() => setConfirmDeleteId(deity.id)} style={{ padding: "4px 8px", color: "red" }}>Delete</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {confirmDeleteId && (
        <ModalEdit open={true} onClose={() => setConfirmDeleteId(null)}>
          <h3>Confirm Delete</h3>
          <p>Are you sure you want to delete this deity?</p>
          <button onClick={() => removeDeity(confirmDeleteId)} style={{ padding: "8px 16px", color: "red" }}>Delete</button>
        </ModalEdit>
      )}
      {editModalOpen && (
        <ModalEdit open={true} onClose={cancelEdit}>
          <form onSubmit={saveEdit}>
            <h3>Edit Deity</h3>
            <input value={editNameEn} onChange={e => setEditNameEn(e.target.value)} style={{ padding: 4, marginBottom: 8 }} />
            <input value={editNameTa} onChange={e => setEditNameTa(e.target.value)} style={{ padding: 4, marginBottom: 8 }} />
            <input value={editImageUrl} onChange={e => setEditImageUrl(e.target.value)} style={{ padding: 4, marginBottom: 8 }} />
            <label>
              Show
              <input type="checkbox" checked={editShow} onChange={e => setEditShow(e.target.checked)} />
            </label>
            <button type="submit" style={{ padding: "8px 16px", marginTop: 8 }}>Save</button>
          </form>
        </ModalEdit>
      )}
    </div>
  );
}

// ContentManager with full UI and features
function ContentManager({ deity, columns, filters }) {
  const [contents, setContents] = useState([]);
  const [type, setType] = useState("audio");
  const [titleEn, setTitleEn] = useState("");
  const [titleTa, setTitleTa] = useState("");
  const [url, setUrl] = useState("");
  const [artist, setArtist] = useState("");
  const [albumArt, setAlbumArt] = useState("");
  const [duration, setDuration] = useState("");
  const [show, setShow] = useState(true);

  // For editing
  const [editingId, setEditingId] = useState(null);
  const [editType, setEditType] = useState("");
  const [editTitleEn, setEditTitleEn] = useState("");
  const [editTitleTa, setEditTitleTa] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [editArtist, setEditArtist] = useState("");
  const [editAlbumArt, setEditAlbumArt] = useState("");
  const [editDuration, setEditDuration] = useState("");
  const [editShow, setEditShow] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Search/filter
  const [search, setSearch] = useState("");
  // Advanced filter
  const [advFilters] = useState(filters);

  const filteredContents = contents.filter(
    c =>
      (c.titleEn.toLowerCase().includes(search.toLowerCase()) ||
        c.titleTa.toLowerCase().includes(search.toLowerCase()) ||
        (c.artist && c.artist.toLowerCase().includes(search.toLowerCase()))) &&
      (!advFilters.type || c.type === advFilters.type) &&
      (!advFilters.show || String(c.show) === advFilters.show) &&
      (!advFilters.artist || (c.artist && c.artist.toLowerCase().includes(advFilters.artist.toLowerCase())))
    // Date filter can be added if you store a createdAt field
  );

  // Confirmation dialog
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Bulk selection
  const bulk = useBulkSelection(filteredContents);

  useEffect(() => {
    if (deity) {
      getDocs(
        query(collection(db, "contents"), where("deityId", "==", deity.id))
      ).then((snapshot) => {
        setContents(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      });
    }
  }, [deity]);

  const addContent = async (e) => {
    e.preventDefault();
    if (url && !isValidUrl(url)) {
      showToast("Please enter a valid URL for audio/video.", "error");
      return;
    }
    if (albumArt && !isValidUrl(albumArt)) {
      showToast("Please enter a valid album art URL.", "error");
      return;
    }
    const docRef = await addDoc(collection(db, "contents"), {
      deityId: deity.id,
      type,
      titleEn,
      titleTa,
      url,
      artist,
      albumArt,
      duration,
      show,
    });
    await logActivity({
      action: "add",
      entity: "content",
      entityId: docRef.id,
      details: { type, titleEn, titleTa, url, artist, albumArt, duration, show },
      user: "admin"
    });
    showToast("Content added!", "success");
    setType("audio");
    setTitleEn("");
    setTitleTa("");
    setUrl("");
    setArtist("");
    setAlbumArt("");
    setDuration("");
    setShow(true);
    getDocs(
      query(collection(db, "contents"), where("deityId", "==", deity.id))
    ).then((snapshot) => {
      setContents(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
  };

  const toggleShow = async (content) => {
    await updateDoc(doc(db, "contents", content.id), { show: !content.show });
    await logActivity({
      action: "edit",
      entity: "content",
      entityId: content.id,
      details: { show: `${content.show} -> ${!content.show}` },
      user: "admin"
    });
    showToast("Content visibility updated!", "info");
    setContents((prev) =>
      prev.map((c) => (c.id === content.id ? { ...c, show: !c.show } : c))
    );
  };

  const removeContent = async (contentId) => {
    await deleteDoc(doc(db, "contents", contentId));
    await logActivity({
      action: "delete",
      entity: "content",
      entityId: contentId,
      details: {},
      user: "admin"
    });
    showToast("Content deleted!", "success");
    setContents((prev) => prev.filter((c) => c.id !== contentId));
    setConfirmDeleteId(null);
  };

  // Bulk actions
  const bulkDelete = async () => {
    for (const id of bulk.selected) {
      await deleteDoc(doc(db, "contents", id));
      await logActivity({
        action: "bulk_delete",
        entity: "content",
        entityId: id,
        details: {},
        user: "admin"
      });
    }
    showToast("Bulk delete completed!", "success");
    setContents(contents.filter(c => !bulk.selected.includes(c.id)));
    bulk.clearAll();
  };

  const bulkHide = async () => {
    for (const id of bulk.selected) {
      await updateDoc(doc(db, "contents", id), { show: false });
      await logActivity({
        action: "bulk_hide",
        entity: "content",
        entityId: id,
        details: { show: "true -> false" },
        user: "admin"
      });
    }
    showToast("Bulk hide completed!", "info");
    setContents(contents.map(c => bulk.selected.includes(c.id) ? { ...c, show: false } : c));
    bulk.clearAll();
  };

  const bulkShow = async () => {
    for (const id of bulk.selected) {
      await updateDoc(doc(db, "contents", id), { show: true });
      await logActivity({
        action: "bulk_show",
        entity: "content",
        entityId: id,
        details: { show: "false -> true" },
        user: "admin"
      });
    }
    showToast("Bulk show completed!", "info");
    setContents(contents.map(c => bulk.selected.includes(c.id) ? { ...c, show: true } : c));
    bulk.clearAll();
  };

  // Edit functionality
  const startEdit = (content) => {
    setEditingId(content.id);
    setEditType(content.type);
    setEditTitleEn(content.titleEn);
    setEditTitleTa(content.titleTa);
    setEditUrl(content.url);
    setEditArtist(content.artist || "");
    setEditAlbumArt(content.albumArt || "");
    setEditDuration(content.duration || "");
    setEditShow(content.show);
    setEditModalOpen(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditModalOpen(false);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (editUrl && !isValidUrl(editUrl)) {
      showToast("Please enter a valid URL for audio/video.", "error");
      return;
    }
    if (editAlbumArt && !isValidUrl(editAlbumArt)) {
      showToast("Please enter a valid album art URL.", "error");
      return;
    }
    await updateDoc(doc(db, "contents", editingId), {
      type: editType,
      titleEn: editTitleEn,
      titleTa: editTitleTa,
      url: editUrl,
      artist: editArtist,
      albumArt: editAlbumArt,
      duration: editDuration,
      show: editShow,
    });
    await logActivity({
      action: "edit",
      entity: "content",
      entityId: editingId,
      details: { type: editType, titleEn: editTitleEn, titleTa: editTitleTa, url: editUrl, artist: editArtist, albumArt: editAlbumArt, duration: editDuration, show: editShow },
      user: "admin"
    });
    showToast("Content updated!", "success");
    setEditingId(null);
    setEditModalOpen(false);
    getDocs(
      query(collection(db, "contents"), where("deityId", "==", deity.id))
    ).then((snapshot) => {
      setContents(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
  };

  // File upload for album art
  const handleAlbumArtUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = await uploadImageFile(file, "album_art");
      setAlbumArt(url);
      showToast("Album art uploaded!", "success");
    }
  };

  return (
    <div className="admin-container">
      <h2>Contents for {deity.nameEn} ({deity.nameTa})</h2>
      <input
        placeholder="Search contents..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 12, padding: 8, width: "100%" }}
      />
      <form onSubmit={addContent} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <select value={type} onChange={(e) => setType(e.target.value)} style={{ padding: 8 }}>
          <option value="audio">Audio</option>
          <option value="video">Video</option>
          <option value="lyric">Lyric</option>
        </select>
        <input
          placeholder="Title (English)"
          value={titleEn}
          onChange={(e) => setTitleEn(e.target.value)}
          required
          style={{ flex: "1 1 180px", padding: 8 }}
        />
        <input
          placeholder="Title (Tamil)"
          value={titleTa}
          onChange={(e) => setTitleTa(e.target.value)}
          required
          style={{ flex: "1 1 180px", padding: 8 }}
        />
        <input
          placeholder="URL / Lyric"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          required
          style={{ flex: "1 1 180px", padding: 8 }}
        />
        <input
          placeholder="Artist"
          value={artist}
          onChange={(e) => setArtist(e.target.value)}
          style={{ flex: "1 1 120px", padding: 8 }}
        />
        <input
          placeholder="Album Art URL"
          value={albumArt}
          onChange={(e) => setAlbumArt(e.target.value)}
          style={{ flex: "1 1 120px", padding: 8 }}
        />
        <input
          type="file"
          accept="image/*"
          onChange={handleAlbumArtUpload}
          style={{ flex: "1 1 120px", padding: 8 }}
        />
        <input
          placeholder="Duration"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          style={{ flex: "1 1 80px", padding: 8 }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
          Show
          <input
            type="checkbox"
            checked={show}
            onChange={(e) => setShow(e.target.checked)}
          />
        </label>
        <button type="submit" style={{ padding: "8px 16px" }}>Add Content</button>
      </form>
      <div style={{ marginBottom: 12 }}>
        <button onClick={bulk.selectAll} style={{ marginRight: 8 }}>Select All</button>
        <button onClick={bulk.clearAll} style={{ marginRight: 8 }}>Clear Selection</button>
        <button onClick={bulkShow} disabled={bulk.selected.length === 0} style={{ marginRight: 8 }}>Bulk Show</button>
        <button onClick={bulkHide} disabled={bulk.selected.length === 0} style={{ marginRight: 8 }}>Bulk Hide</button>
        <button onClick={bulkDelete} disabled={bulk.selected.length === 0} style={{ color: "red" }}>Bulk Delete</button>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#f7f7f7" }}>
            <th></th>
            {columns.type && <th style={{ padding: 8, border: "1px solid #ddd" }}>Type</th>}
            {columns.titleEn && <th style={{ padding: 8, border: "1px solid #ddd" }}>Title (English)</th>}
            {columns.titleTa && <th style={{ padding: 8, border: "1px solid #ddd" }}>Title (Tamil)</th>}
            {columns.artist && <th style={{ padding: 8, border: "1px solid #ddd" }}>Artist</th>}
            {columns.duration && <th style={{ padding: 8, border: "1px solid #ddd" }}>Duration</th>}
            {columns.show && <th style={{ padding: 8, border: "1px solid #ddd" }}>Show</th>}
            {columns.actions && <th style={{ padding: 8, border: "1px solid #ddd" }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {filteredContents.map((content) => (
            <tr key={content.id} style={{ background: content.show ? "#fff" : "#f9e0e0" }}>
              <td>
                <input
                  type="checkbox"
                  checked={bulk.selected.includes(content.id)}
                  onChange={() => bulk.toggle(content.id)}
                />
              </td>
              {columns.type && (
                <td style={{ padding: 8, border: "1px solid #ddd" }}>{content.type}</td>
              )}
              {columns.titleEn && (
                <td style={{ padding: 8, border: "1px solid #ddd" }}>{content.titleEn}</td>
              )}
              {columns.titleTa && (
                <td style={{ padding: 8, border: "1px solid #ddd" }}>{content.titleTa}</td>
              )}
              {columns.artist && (
                <td style={{ padding: 8, border: "1px solid #ddd" }}>{content.artist}</td>
              )}
              {columns.duration && (
                <td style={{ padding: 8, border: "1px solid #ddd" }}>{content.duration}</td>
              )}
              {columns.show && (
                <td style={{ padding: 8, border: "1px solid #ddd" }}>
                  <button onClick={() => toggleShow(content)} style={{ padding: "4px 8px" }}>
                    {content.show ? "Hide" : "Show"}
                  </button>
                </td>
              )}
              {columns.actions && (
                <td style={{ padding: 8, border: "1px solid #ddd" }}>
                  <button onClick={() => startEdit(content)} style={{ padding: "4px 8px", color: "blue" }}>Edit</button>
                  <button onClick={() => setConfirmDeleteId(content.id)} style={{ padding: "4px 8px", color: "red" }}>Delete</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {confirmDeleteId && (
        <ModalEdit open={true} onClose={() => setConfirmDeleteId(null)}>
          <h3>Confirm Delete</h3>
          <p>Are you sure you want to delete this content?</p>
          <button onClick={() => removeContent(confirmDeleteId)} style={{ padding: "8px 16px", color: "red" }}>Delete</button>
        </ModalEdit>
      )}
      {editModalOpen && (
        <ModalEdit open={true} onClose={cancelEdit}>
          <form onSubmit={saveEdit}>
            <h3>Edit Content</h3>
            <select value={editType} onChange={e => setEditType(e.target.value)} style={{ padding: 4, marginBottom: 8 }}>
              <option value="audio">Audio</option>
              <option value="video">Video</option>
              <option value="lyric">Lyric</option>
            </select>
            <input value={editTitleEn} onChange={e => setEditTitleEn(e.target.value)} style={{ padding: 4, marginBottom: 8 }} />
            <input value={editTitleTa} onChange={e => setEditTitleTa(e.target.value)} style={{ padding: 4, marginBottom: 8 }} />
            <input value={editUrl} onChange={e => setEditUrl(e.target.value)} style={{ padding: 4, marginBottom: 8 }} />
            <input value={editArtist} onChange={e => setEditArtist(e.target.value)} style={{ padding: 4, marginBottom: 8 }} />
            <input value={editAlbumArt} onChange={e => setEditAlbumArt(e.target.value)} style={{ padding: 4, marginBottom: 8 }} />
            <input value={editDuration} onChange={e => setEditDuration(e.target.value)} style={{ padding: 4, marginBottom: 8 }} />
            <label>
              Show
              <input type="checkbox" checked={editShow} onChange={e => setEditShow(e.target.checked)} />
            </label>
            <button type="submit" style={{ padding: "8px 16px", marginTop: 8 }}>Save</button>
          </form>
        </ModalEdit>
      )}
      <DragDropContentManager deity={deity} />
    </div>
  );
}

export default function App() {
  const [selectedDeity, setSelectedDeity] = useState(null);
  const [dark, setDark] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [columns, setColumns] = useState({
    nameEn: true,
    nameTa: true,
    icon: true,
    show: true,
    actions: true,
    type: true,
    titleEn: true,
    titleTa: true,
    artist: true,
    duration: true,
  });
  const [filters, setFilters] = useState({
    type: "",
    show: "",
    artist: "",
    date: "",
  });

  return (
    <div className={`admin-container${dark ? " dark" : ""}`}>
      <DarkModeToggle dark={dark} setDark={setDark} />
      <button onClick={() => setShowAudit(a => !a)} style={{ marginBottom: 16 }}>
        {showAudit ? "Hide Audit Trail" : "Show Audit Trail"}
      </button>
      <ToastContainer />
      <ColumnSettings columns={columns} setColumns={setColumns} />
      <AdvancedFilter filters={filters} setFilters={setFilters} />
      <button
        onClick={async () => {
          const data = await exportData();
          showToast("Exported data as JSON!", "info");
          // Download as file
          const blob = new Blob([data], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "export.json";
          a.click();
          URL.revokeObjectURL(url);
        }}
        style={{ marginBottom: 16 }}
      >
        Export Data
      </button>
      <input
        type="file"
        accept="application/json"
        onChange={async e => {
          const file = e.target.files[0];
          if (file) {
            const text = await file.text();
            await importData(text);
            showToast("Imported data from JSON!", "success");
          }
        }}
        style={{ marginBottom: 16 }}
      />
      {showAudit && <AuditTrail />}
      {!selectedDeity ? (
        <DeityManager onSelectDeity={setSelectedDeity} columns={columns} />
      ) : (
        <>
          <button onClick={() => setSelectedDeity(null)} style={{ marginBottom: 16, padding: "8px 16px" }}>
            Back to Deities
          </button>
          <ContentManager deity={selectedDeity} columns={columns} filters={filters} />
        </>
      )}
    </div>
  );
}